import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

export default function Upload() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState("");
  const [newAlbumName, setNewAlbumName] = useState("");
  const [newAlbumDate, setNewAlbumDate] = useState("");
  const [newAlbumDesc, setNewAlbumDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => { fetchAlbums(); }, []);

  async function fetchAlbums() {
    const { data } = await supabase.from("albums").select("*").order("date", { ascending: false });
    setAlbums(data || []);
  }

  async function createAlbum(e) {
    e.preventDefault();
    setCreatingAlbum(true);
    const { data, error } = await supabase
      .from("albums")
      .insert({ name: newAlbumName, date: newAlbumDate, description: newAlbumDesc })
      .select()
      .single();

    if (!error) {
      setAlbums((prev) => [data, ...prev]);
      setSelectedAlbum(data.id);
      setNewAlbumName("");
      setNewAlbumDate("");
      setNewAlbumDesc("");
      setMessage({ type: "success", text: `Album "${data.name}" created!` });
    } else {
      setMessage({ type: "error", text: error.message });
    }
    setCreatingAlbum(false);
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!selectedAlbum) { setMessage({ type: "error", text: "Please select or create an album first." }); return; }
    if (files.length === 0) { setMessage({ type: "error", text: "Please select at least one photo." }); return; }

    setUploading(true);
    setMessage(null);
    setUploadProgress(0);
    let successCount = 0;

    const headers = await getAuthHeaders();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("photo", file);

      try {
        const res = await fetch("/api/upload", {
          method: "POST",
          headers,
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          const actualFilename = data.filenames[0];

          await supabase.from("photos").insert({
            album_id: selectedAlbum,
            filename: file.name,
            file_path: actualFilename,
            caption: caption || null,
          });
          successCount++;
        }
      } catch (err) {
        console.error("Upload error:", err);
      }

      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setMessage({ type: "success", text: `${successCount} of ${files.length} photo(s) uploaded successfully!` });
    setFiles([]);
    setCaption("");
    setUploadProgress(0);
    setUploading(false);
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Upload Photos</h1>
        <p className="page-subtitle">Admin only</p>
      </div>

      {message && (
        <div className={`message-banner ${message.type === "success" ? "message-success" : "error-banner"}`}>
          {message.type === "success" ? "✅" : "⚠️"} {message.text}
        </div>
      )}

      {/* Create Album */}
      <div className="upload-section">
        <h2 className="section-label">Create New Album</h2>
        <form className="album-form" onSubmit={createAlbum}>
          <input
            className="form-input"
            type="text"
            placeholder="Album name (e.g. First Week Home)"
            value={newAlbumName}
            onChange={(e) => setNewAlbumName(e.target.value)}
            required
          />
          <input
            className="form-input"
            type="date"
            value={newAlbumDate}
            onChange={(e) => setNewAlbumDate(e.target.value)}
            required
          />
          <input
            className="form-input"
            type="text"
            placeholder="Description (optional)"
            value={newAlbumDesc}
            onChange={(e) => setNewAlbumDesc(e.target.value)}
          />
          <button className="create-album-btn" type="submit" disabled={creatingAlbum}>
            {creatingAlbum ? "Creating..." : "Create Album"}
          </button>
        </form>
      </div>

      {/* Upload Photos */}
      <div className="upload-section">
        <h2 className="section-label">Upload Photos</h2>
        <form className="upload-form" onSubmit={handleUpload}>
          <div className="form-group">
            <label className="form-label">Select Album</label>
            <select
              className="form-input"
              value={selectedAlbum}
              onChange={(e) => setSelectedAlbum(e.target.value)}
              required
            >
              <option value="">Choose an album...</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{a.name} — {new Date(a.date).toLocaleDateString()}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Photos</label>
            <div
              className="drop-zone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)); }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="file-input"
                id="file-input"
                onChange={(e) => setFiles(Array.from(e.target.files))}
              />
              <label htmlFor="file-input" className="drop-label">
                {files.length > 0
                  ? `${files.length} photo(s) selected`
                  : "📸 Drag & drop or click to select photos"}
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Caption (optional)</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. First day home!"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>

          {/* Preview */}
          {files.length > 0 && (
            <div className="preview-grid">
              {files.map((file, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="preview-img"
                />
              ))}
            </div>
          )}

          {/* Progress bar */}
          {uploading && uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
              <span className="upload-progress-text">{uploadProgress}%</span>
            </div>
          )}

          <button className="upload-btn" type="submit" disabled={uploading}>
            {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${files.length > 0 ? files.length + " Photo(s)" : "Photos"}`}
          </button>
        </form>
      </div>
    </div>
  );
}
